import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Loader2, Plus, Trash2, ShieldOff, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin dialog to manage IPs that bypass every rate-limit (login, register,
 * 2FA, LLM helpers...). Used by the founders while running the pilot so they
 * don't get accidentally locked out while testing.
 */
export const WhitelistedIpsDialog = ({ open, onClose }) => {
  const [ips, setIps] = useState([]);
  const [yourIp, setYourIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/whitelisted-ips`, { withCredentials: true });
      setIps(res.data?.ips || []);
      setYourIp(res.data?.your_ip || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const add = async (ip, label = '') => {
    if (!ip?.trim()) {
      toast.error('Introduce una IP');
      return;
    }
    setAdding(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/whitelisted-ips`,
        { ip: ip.trim(), label: label.trim() },
        { withCredentials: true }
      );
      toast.success(`IP ${ip} añadida`);
      setNewIp('');
      setNewLabel('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo añadir');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (ip) => {
    if (!window.confirm(`¿Quitar la IP ${ip} de la lista?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/whitelisted-ips/${encodeURIComponent(ip)}`, { withCredentials: true });
      toast.success('Eliminada');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const alreadyHasYourIp = !!ips.find((e) => e.ip === yourIp);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="whitelisted-ips-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-amber-600" />
            IPs sin límite
          </DialogTitle>
          <DialogDescription>
            Las IPs de esta lista no son afectadas por el rate-limit de login, registro ni de la IA. Útil para hacer pruebas sin bloqueos.
          </DialogDescription>
        </DialogHeader>

        {/* Your current IP banner */}
        {yourIp && (
          <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm">
            <div className="min-w-0">
              <p className="text-xs text-blue-700 font-medium">Tu IP actual</p>
              <p className="font-mono text-sm text-blue-900 truncate">{yourIp}</p>
            </div>
            {alreadyHasYourIp ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Ya en la lista</Badge>
            ) : (
              <Button
                size="sm"
                onClick={() => add(yourIp, 'Mi IP actual')}
                disabled={adding}
                className="gap-1"
                data-testid="add-my-ip-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Añadir esta IP
              </Button>
            )}
          </div>
        )}

        {/* Manual add */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Nueva IP</Label>
            <Input
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              placeholder="83.123.45.67"
              className="font-mono text-sm"
              data-testid="new-ip-input"
            />
          </div>
          <div>
            <Label className="text-xs">Etiqueta (opcional)</Label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Casa / Móvil / Oficina"
              className="text-sm"
            />
          </div>
          <Button
            onClick={() => add(newIp, newLabel)}
            disabled={adding}
            className="gap-1"
            data-testid="add-ip-btn"
          >
            {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Añadir
          </Button>
        </div>

        {/* Existing list */}
        <div className="border rounded-md max-h-72 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : ips.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground italic">No hay IPs en la lista.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs">
                <tr>
                  <th className="text-left p-2">IP</th>
                  <th className="text-left p-2">Etiqueta</th>
                  <th className="text-left p-2 hidden sm:table-cell">Añadida</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {ips.map((entry) => (
                  <tr key={entry.ip} className="border-t hover:bg-muted/30" data-testid={`ip-row-${entry.ip}`}>
                    <td className="p-2 font-mono text-xs flex items-center gap-1">
                      {entry.ip}
                      {entry.ip === yourIp && (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">tú</Badge>
                      )}
                    </td>
                    <td className="p-2 text-xs">{entry.label || '—'}</td>
                    <td className="p-2 text-xs text-muted-foreground hidden sm:table-cell">
                      {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="p-2">
                      <Button
                        size="sm" variant="ghost"
                        className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                        onClick={() => remove(entry.ip)}
                        data-testid={`remove-ip-${entry.ip}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <p className="text-[11px] text-muted-foreground flex-1">
            <Copy className="w-3 h-3 inline-block mr-1" />
            Para añadir otro PC, abre <span className="font-mono">api.ipify.org</span> desde ese PC y copia la IP.
          </p>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhitelistedIpsDialog;
